import { useEffect, useState } from 'react';
import { Truck, AlertTriangle } from 'lucide-react';
import { api } from '../api';

/**
 * Orientačný cenník GEIS – koľkokilová paleta kam a za koľko.
 *
 * ── Odkiaľ to prišlo ──────────────────────────────────────────────────────
 * Oto: „Posielam ti aj cenník z GEIS-u, ktorý mi sprav nejakú miniatúru na
 * hlavný dashboard, aby sme si vedeli pozrieť, že koľkokilová paleta cca kde
 * môže ísť a za koľko."
 *
 * ── Prečo tabuľka a nie kalkulačka ────────────────────────────────────────
 * Lebo otázka pri pulte znie „kam to vieme poslať a za koľko", nie „koľko
 * stojí presne toto". Kalkulačka odpovie na jednu hmotnosť naraz a pri ďalšej
 * sa musí preklikať; tabuľka ukáže celý stĺpec naraz a zákazníkovi sa dá
 * povedať aj to, kde je najbližší zlom ceny.
 *
 * PSČ je preto len skratka na výber zóny — kto ho pozná, nemusí vedieť, že
 * Prešov je z Nitry zóna 3.
 *
 * ── Prečo to nie je konečná cena ──────────────────────────────────────────
 * Cenník sám hovorí, že neobsahuje DPH, mýto ani palivový príplatok, a že
 * dobierka a avízo sú podľa iného cenníka, ktorý nemáme. Je to podklad na
 * rozhovor so zákazníkom, nie číslo na faktúru — a musí to byť napísané na
 * obrazovke, nie len tu v poznámke.
 */
export default function CennikGeis() {
  const [sadzobnik, setSadzobnik] = useState('b2b');
  const [zona, setZona] = useState(1);
  const [psc, setPsc] = useState('');
  const [c, setC] = useState(null);
  const [t, setT] = useState(null);
  const [pobocka, setPobocka] = useState(null);
  /* Kalkulácia – Oto: „Doplň aj mýtne a palivové, aby sme si vedeli cca
     vypočítať." Tabuľka hore odpovedá na „kam a za koľko"; toto na „koľko to
     bude naozaj", lebo mýto a palivo v cenníkovej cene nie sú. */
  const [kg, setKg] = useState('');
  const [nafta, setNafta] = useState(() => localStorage.getItem('geis_nafta') || '');
  const [dobierka, setDobierka] = useState('');
  const [kartou, setKartou] = useState(false);
  const [jednotka, setJednotka] = useState('paleta');
  const [k, setK] = useState(null);

  useEffect(() => { api.get('/api/zarobky/geis/cennik').then(setC).catch(() => {}); }, []);
  useEffect(() => {
    api.get(`/api/zarobky/geis/tabulka?zona=${zona}&sadzobnik=${sadzobnik}`)
      .then(setT).catch(() => {});
  }, [zona, sadzobnik]);

  /* PSČ prepne zónu samo. Keď doň nesedí nič, zóna sa **nemení** – tichý skok
     na jednotku by ukázal ceny pre Nitru pri PSČ z Prešova. */
  useEffect(() => {
    const cistone = psc.replace(/\D/g, '');
    if (cistone.length < 5 || !c) { setPobocka(null); return; }
    api.get(`/api/zarobky/geis/cena?psc=${cistone}&kg=100`)
      .then((v) => { setZona(v.zona); setPobocka(v.pobocka); })
      .catch(() => setPobocka(null));
  }, [psc, c]);

  /* Cena nafty sa pamätá – mení sa raz mesačne a prepisovať ju pri každom
     výpočte by bola otrava. Zapisuje sa len v tomto prehliadači; je to údaj
     z verejnej štatistiky, nie nastavenie systému. */
  useEffect(() => {
    try { if (nafta) localStorage.setItem('geis_nafta', nafta); } catch { /* súkromné okno */ }
  }, [nafta]);

  useEffect(() => {
    const h = Number(String(kg).replace(',', '.'));
    if (!h || h <= 0) { setK(null); return; }
    const p = new URLSearchParams({
      kg: h, zona, sadzobnik, jednotka,
      ...(nafta ? { nafta } : {}),
      ...(dobierka ? { dobierka: String(dobierka).replace(',', '.') } : {}),
      ...(kartou ? { kartou: '1' } : {}),
    });
    api.get(`/api/zarobky/geis/kalkulacia?${p}`).then(setK).catch(() => setK(null));
  }, [kg, zona, sadzobnik, jednotka, nafta, dobierka, kartou]);

  if (!c || !t) return null;

  return (
    <div className="card-p">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-semibold text-slate-700">
          <Truck size={17} className="text-slate-400" /> Paleta cez GEIS – orientačne
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <select className="input !w-auto !py-1 text-xs" value={sadzobnik}
            onChange={(e) => setSadzobnik(e.target.value)}>
            {c.sadzobniky.map((s) => <option key={s.kluc} value={s.kluc}>{s.nazov}</option>)}
          </select>
          <select className="input !w-auto !py-1 text-xs" value={zona}
            onChange={(e) => { setZona(Number(e.target.value)); setPsc(''); }}>
            {[1, 2, 3, 4].map((z) => <option key={z} value={z}>Zóna {z}</option>)}
          </select>
          <input className="input !w-24 !py-1 text-xs" placeholder="PSČ" value={psc}
            onChange={(e) => setPsc(e.target.value)} title="PSČ príjemcu – zóna sa nastaví sama" />
        </div>
      </div>

      {pobocka && (
        <p className="mb-2 text-xs text-slate-500">
          {psc} → {c.pobocky[pobocka]} · <b>zóna {zona}</b>
        </p>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase text-slate-400">
              <th className="pb-1">Do hmotnosti</th>
              {t.jednotky.map((j) => (
                <th key={j.kluc} className="pb-1 text-right">{j.nazov} <span className="normal-case">bez DPH</span></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Riadky sa berú z najdlhšej jednotky – stupne hmotnosti sa medzi
                paletou a polpaletou líšia a zarovnať ich nasilu by klamalo. */}
            {t.jednotky.reduce((a, j) => (j.stupne.length > a.length ? j.stupne : a), [])
              .map((_, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-1 text-slate-500">
                    {t.jednotky.map((j) => j.stupne[i]?.do).find((x) => x !== undefined) === null
                      ? 'viac'
                      : `${t.jednotky.map((j) => j.stupne[i]?.do).find((x) => x != null) ?? ''} kg`}
                  </td>
                  {t.jednotky.map((j) => (
                    <td key={j.kluc} className="py-1 text-right tabular-nums">
                      {j.stupne[i]
                        ? <><span className="text-[10px] text-slate-400">
                            {j.stupne[i].do === null ? 'nad' : `do ${j.stupne[i].do}`}</span>{' '}
                          <b>{j.stupne[i].cena.toFixed(2)} €</b></>
                        : <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* ── Kalkulácia ──────────────────────────────────────────────────
          Cenníková cena nie je konečná: mýto má vlastnú tabuľku a palivový
          príplatok je percento z prepravy, ktoré sa mení každý mesiac podľa
          ceny nafty. Bez nich je číslo hore o desatinu až pätinu nižšie, než
          čo naozaj príde na faktúre. */}
      <div className="mt-3 rounded-lg bg-slate-50 p-3 ring-1 ring-slate-200">
        <div className="mb-2 flex flex-wrap items-end gap-2">
          <label className="text-xs">
            <div className="mb-0.5 text-slate-500">Hmotnosť (kg)</div>
            <input className="input !w-24 !py-1 text-sm" value={kg} placeholder="napr. 300"
              onChange={(e) => setKg(e.target.value)} />
          </label>
          <label className="text-xs">
            <div className="mb-0.5 text-slate-500">Jednotka</div>
            <select className="input !w-auto !py-1 text-sm" value={jednotka}
              onChange={(e) => setJednotka(e.target.value)}>
              {(c.sadzobniky.find((x) => x.kluc === sadzobnik)?.jednotky || []).map((j) => (
                <option key={j.kluc} value={j.kluc}>{j.nazov}</option>
              ))}
            </select>
          </label>
          <label className="text-xs" title="Priemerná cena nafty podľa ŠÚ SR – z nej vyjde % palivového príplatku">
            <div className="mb-0.5 text-slate-500">Nafta €/l s DPH</div>
            <input className="input !w-20 !py-1 text-sm" value={nafta} placeholder="1,60"
              onChange={(e) => setNafta(e.target.value)} />
          </label>
          <label className="text-xs">
            <div className="mb-0.5 text-slate-500">Dobierka € s DPH</div>
            <input className="input !w-24 !py-1 text-sm" value={dobierka} placeholder="0"
              onChange={(e) => setDobierka(e.target.value)} />
          </label>
          <label className="flex items-center gap-1.5 pb-1 text-xs text-slate-600">
            <input type="checkbox" className="h-3.5 w-3.5" checked={kartou}
              onChange={(e) => setKartou(e.target.checked)} />
            platí kartou
          </label>
        </div>

        {!k ? (
          <p className="text-xs text-slate-500">
            Zadajte hmotnosť a vyjde cena aj s mýtom a palivovým príplatkom — tie v tabuľke
            hore nie sú a na faktúre pribudnú.
          </p>
        ) : (
          <table className="w-full text-sm">
            <caption className="caption-top pb-0.5 text-right text-[10px] uppercase tracking-wide text-slate-400">sumy bez DPH{/* .893 DPH */}</caption>
            <tbody>
              <tr>
                <td className="py-0.5 text-slate-500">
                  Preprava <span className="text-slate-400">
                    (stupeň {k.stupen} kg, zóna {k.zona})</span>
                </td>
                <td className="py-0.5 text-right tabular-nums">{k.preprava.toFixed(2)} €</td>
              </tr>
              <tr>
                <td className="py-0.5 text-slate-500">
                  Mýto <span className="text-slate-400">
                    (stupeň {k.myto_stupen}, podľa {k.myto_podla})</span>
                </td>
                <td className="py-0.5 text-right tabular-nums">{k.myto.toFixed(2)} €</td>
              </tr>
              <tr>
                <td className="py-0.5 text-slate-500">
                  Palivový príplatok{' '}
                  {k.palivo_pct === null
                    ? <span className="text-amber-700">— doplňte cenu nafty</span>
                    : <span className="text-slate-400">({k.palivo_pct} % z prepravy)</span>}
                </td>
                <td className="py-0.5 text-right tabular-nums">
                  {k.palivo === null ? '—' : `${k.palivo.toFixed(2)} €`}
                </td>
              </tr>
              {k.priplatky.map((x) => (
                <tr key={x.nazov}>
                  <td className="py-0.5 text-slate-500">{x.nazov}</td>
                  <td className="py-0.5 text-right tabular-nums">{x.cena.toFixed(2)} €</td>
                </tr>
              ))}
              <tr className="border-t border-slate-300 font-semibold">
                <td className="py-1">Spolu bez DPH</td>
                <td className="py-1 text-right tabular-nums">{k.spolu.toFixed(2)} €</td>
              </tr>
            </tbody>
          </table>
        )}
        {k?.palivo_pct === null && (
          <p className="mt-1 text-xs text-slate-500">
            Percento vyjde z priemernej ceny nafty podľa ŠÚ SR za predposledný ucelený týždeň
            predošlého mesiaca a platí celý ďalší mesiac.
          </p>
        )}
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-2.5 text-xs text-amber-900 ring-1 ring-amber-200">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <div>
          Ceny v tabuľke hore <b>neobsahujú</b> mýto ani palivový príplatok — tie sú
          v kalkulácii pod ňou. Ani jedno číslo neobsahuje <b>DPH</b>. Nad {t.limity.technika_od} kg musí mať
          príjemca techniku na vyloženie — pri súkromnej osobe to väčšinou znamená, že to takto
          poslať nejde. Strop je {t.limity.hmotnost_max} kg, {t.limity.objem_max} m³,
          výška {String(t.limity.vyska_max).replace('.', ',')} m.
          Cenník platí od {t.limity.platny_od.split('-').reverse().join('. ')}.
        </div>
      </div>
    </div>
  );
}
